import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useListingActions } from '../../../hooks/useListingAdmin';
import { Contact } from '../../../types';
import {
  checkListingFields,
  cleanContacts,
  LISTING_LIMITS,
  moveItem,
  newId,
  normalizeWebsite,
} from '../../../utils/listingEdit';
import { BilingualField, ErrorList, SaveBar, TextField, useFormDraft } from './formFields';
import type { TabProps } from './ListingEditor';

const ICON_BUTTON =
  'inline-flex items-center justify-center w-11 h-11 rounded-[10px] border border-ink/20 text-ink hover:border-ink/50 disabled:opacity-30';

export default function ContactsTab({ listing }: TabProps) {
  const { t } = useTranslation();
  const { updateListing, saving } = useListingActions();
  const { draft, update, dirty, reset, save, justSaved, failed } = useFormDraft<{ contacts: Contact[] }>({
    contacts: listing.contacts ?? [],
  });
  const [errors, setErrors] = useState<string[]>([]);
  const contacts = draft.contacts;
  const setContacts = (next: Contact[]) => update({ contacts: next });
  const setField = (i: number, patch: Partial<Contact>) =>
    setContacts(contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const handleSave = async () => {
    const cleaned = cleanContacts(contacts).map((c) => (c.website ? { ...c, website: normalizeWebsite(c.website) } : c));
    const problems = checkListingFields({
      category: listing.category,
      subcategory: 'subcategory' in listing ? listing.subcategory : undefined,
      nameHe: listing.nameHe,
      contacts: cleaned,
    });
    setErrors(problems);
    if (problems.length) return;
    await save(() => updateListing(listing.id, { contacts: cleaned }));
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-ash">{t('directory.admin.contacts.hint')}</p>

      {contacts.length === 0 && <p className="text-sm text-ink">{t('directory.admin.contacts.none')}</p>}

      <ol className="space-y-4">
        {contacts.map((c, i) => (
          <li key={c.id} className="rounded-[16px] border border-ink/10 p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-ink">
                {i === 0 ? t('directory.admin.contacts.main') : t('directory.admin.contacts.number', { n: i + 1 })}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={ICON_BUTTON}
                  disabled={i === 0}
                  onClick={() => setContacts(moveItem(contacts, i, -1))}
                  aria-label={t('directory.admin.moveUp')}
                >
                  <ArrowUp className="w-4 h-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className={ICON_BUTTON}
                  disabled={i === contacts.length - 1}
                  onClick={() => setContacts(moveItem(contacts, i, 1))}
                  aria-label={t('directory.admin.moveDown')}
                >
                  <ArrowDown className="w-4 h-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className={`${ICON_BUTTON} text-danger-600`}
                  onClick={() => setContacts(contacts.filter((_, j) => j !== i))}
                  aria-label={t('directory.admin.remove')}
                >
                  <Trash2 className="w-4 h-4" aria-hidden />
                </button>
              </div>
            </div>
            <BilingualField
              labelHe={`${t('directory.field.contactName')} (${t('directory.admin.hebrew')})`}
              labelEn={`${t('directory.field.contactName')} (${t('directory.admin.english')})`}
              valueHe={c.nameHe ?? ''}
              valueEn={c.nameEn ?? ''}
              onChangeHe={(v) => setField(i, { nameHe: v })}
              onChangeEn={(v) => setField(i, { nameEn: v })}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField
                label={t('directory.field.phone')}
                type="tel"
                dir="ltr"
                value={c.phone ?? ''}
                onChange={(v) => setField(i, { phone: v })}
                placeholder="050-1234567"
              />
              <TextField
                label={t('directory.field.whatsapp')}
                type="tel"
                dir="ltr"
                value={c.whatsapp ?? ''}
                onChange={(v) => setField(i, { whatsapp: v })}
                placeholder="050-1234567"
                hint={t('directory.admin.contacts.whatsappHint')}
              />
              <TextField
                label={t('directory.field.email')}
                type="email"
                dir="ltr"
                value={c.email ?? ''}
                onChange={(v) => setField(i, { email: v })}
              />
              <TextField
                label={t('directory.field.website')}
                type="url"
                dir="ltr"
                value={c.website ?? ''}
                onChange={(v) => setField(i, { website: v })}
                placeholder="example.com"
              />
            </div>
          </li>
        ))}
      </ol>

      {contacts.length < LISTING_LIMITS.contacts && (
        <button
          type="button"
          onClick={() => setContacts([...contacts, { id: newId() }])}
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] border border-ink/20 text-sm text-ink hover:border-ink/50"
        >
          <Plus className="w-4 h-4" aria-hidden />
          {t('directory.admin.contacts.add')}
        </button>
      )}

      <ErrorList errors={errors} />
      <SaveBar
        dirty={dirty}
        saving={saving}
        onSave={handleSave}
        onCancel={() => {
          setErrors([]);
          reset();
        }}
        justSaved={justSaved}
        failed={failed}
      />
    </div>
  );
}
